import type { LaddersBoard, LaddersLevel, Ladder, Snake } from "./types";

const S = (pairs: readonly (readonly [number, number])[]): Snake[] =>
  pairs.map(([head, tail]) => ({ head, tail }));
const L = (pairs: readonly (readonly [number, number])[]): Ladder[] =>
  pairs.map(([foot, top]) => ({ foot, top }));

/**
 * THE متوسط BOARDS — round 1's six (ROUND 2: they became the medium level, unchanged). Each game
 * picks one map at random from its level (the engine, from the seeded RNG).
 *
 * ⚠️ APPEND-ONLY AND FROZEN. A board's id, its snakes and ladders, AND THEIR ORDER are its
 * identity: the web draws snake i with skin i and ladder i with material i, and a saved game
 * carries its board whole. To change a board, add a new id. `test/boards.test.ts` runs
 * `boardProblems` over every entry and pins each one's expected length.
 *
 * Expected rolls for one player from the start to 100 (bonus rolls counted), measured by
 * `expectedRolls` under the operator's overshoot rule (a roll past 100 stays put): pinned in
 * `test/boards.test.ts`. The layouts were drawn under an earlier bounce rule and kept for it (the
 * operator: change nothing beyond what the rule needs).
 *
 * THE PRE-SHIP EXCEPTION — UNTIL THE FIRST PROD PUSH (card-86): while no game has been played or
 * saved on prod, a board may still be nudged in place. 21 endpoints moved by a cell or two so that
 * nothing is DRAWN on another — `boardProblems`' «collinear» and «touch» (b5's three ladders up the
 * left edge read as one; b2's ladder 3→22 ended on 19→39's body) — and then, after the card-86
 * review (2026-11-03), b3's three snakes that ran side by side and over the right edge's two
 * ladders: 50→15 → 48→15, 68→30 → 53→30, 88→46 → 88→47 (`apps/web/test/ladders-geometry.test.ts`
 * pins the drawn bodies clear). From the first prod push on, the append-only rule above holds.
 */
const MEDIUM: readonly LaddersBoard[] = [
  {
    // The classic: one long ladder (28→84) and one long snake (87→24) cross the middle.
    id: "b1",
    snakes: S([
      [16, 6],
      [46, 26],
      [49, 30],
      [62, 19],
      [64, 60],
      [87, 24],
      [93, 73],
      [95, 75],
    ]),
    ladders: L([
      [4, 14],
      [9, 31],
      [21, 58],
      [28, 84],
      [36, 44],
      [51, 67],
      [71, 91],
      [78, 98],
      [56, 77],
    ]),
  },
  {
    // Many steady ladders, a snake on nearly every row.
    id: "b2",
    snakes: S([
      [27, 5],
      [43, 18],
      [55, 34],
      [69, 48],
      [83, 61],
      [91, 70],
      [95, 76],
    ]),
    ladders: L([
      [3, 22],
      [7, 46],
      [19, 40],
      [33, 52],
      [45, 65],
      [57, 77],
      [63, 84],
      [72, 94],
      [80, 99],
    ]),
  },
  {
    // Long climbs, long falls: every snake spans three rows or more.
    id: "b3",
    snakes: S([
      [35, 10],
      [48, 15],
      [53, 30],
      [74, 36],
      [88, 47],
      [93, 56],
    ]),
    ladders: L([
      [2, 23],
      [28, 52],
      [29, 70],
      [37, 58],
      [57, 85],
      [63, 82],
      [75, 95],
    ]),
  },
  {
    // A quiet start and a crowded top half.
    id: "b4",
    snakes: S([
      [18, 7],
      [35, 15],
      [66, 44],
      [86, 64],
      [90, 49],
      [96, 72],
    ]),
    ladders: L([
      [5, 25],
      [11, 48],
      [23, 57],
      [39, 60],
      [42, 78],
      [54, 73],
      [61, 83],
      [77, 98],
    ]),
  },
  {
    // The quick board: ladders up the edges, snakes down the middle.
    id: "b5",
    snakes: S([
      [26, 4],
      [44, 23],
      [53, 32],
      [66, 47],
      [74, 55],
      [84, 57],
      [92, 69],
    ]),
    ladders: L([
      [6, 34],
      [10, 30],
      [20, 42],
      [40, 62],
      [51, 71],
      [60, 81],
      [67, 88],
      [79, 99],
    ]),
  },
  {
    // The zigzag: eight snakes, eight ladders, evenly spread.
    id: "b6",
    snakes: S([
      [24, 3],
      [38, 17],
      [47, 27],
      [56, 14],
      [70, 43],
      [86, 64],
      [89, 68],
      [95, 77],
    ]),
    ladders: L([
      [7, 29],
      [15, 36],
      [22, 58],
      [31, 51],
      [49, 72],
      [62, 83],
      [71, 92],
      [78, 96],
    ]),
  },
];

/**
 * ROUND 2 (card-86 planner-2, 2026-11-03) — THE آسان AND سخت MAPS: found by a seeded search under
 * the SAME gates as the six above (`boardProblems` with the level's band — crossings, «collinear»,
 * «touch», traps — and `apps/web/test/ladders-geometry.test.ts`'s drawn-body clearance), and then
 * frozen like them (append-only from the first prod push; the pre-ship exception above applies until
 * then). آسان: long ladders, few and short snakes; سخت: long snakes, few and short ladders. Every
 * snake and ladder of these twelve spans at least TWO rows (no one-row stubs — the search's floor).
 * Expected rolls are pinned in `test/boards.test.ts`.
 */
const EASY: readonly LaddersBoard[] = [
  {
    // 5 snakes (rows 2–3), 7 ladders (rows 2–5) — 26.55 rolls.
    id: "e1",
    snakes: S([
      [92, 69],
      [95, 73],
      [61, 45],
      [70, 48],
      [34, 17],
    ]),
    ladders: L([
      [50, 62],
      [7, 57],
      [16, 59],
      [19, 60],
      [76, 93],
      [6, 44],
      [77, 97],
    ]),
  },
  {
    // 5 snakes (rows 2–3), 7 ladders (rows 2–5) — 27.66 rolls.
    id: "e2",
    snakes: S([
      [37, 2],
      [44, 14],
      [78, 56],
      [72, 51],
      [49, 18],
    ]),
    ladders: L([
      [39, 75],
      [7, 29],
      [63, 81],
      [47, 70],
      [11, 32],
      [46, 92],
      [20, 41],
    ]),
  },
  {
    // 6 snakes (rows 2–3), 9 ladders (rows 2–5) — 27.23 rolls.
    id: "e3",
    snakes: S([
      [48, 30],
      [61, 40],
      [91, 72],
      [95, 74],
      [67, 46],
      [43, 19],
    ]),
    ladders: L([
      [56, 93],
      [7, 54],
      [65, 94],
      [21, 42],
      [59, 83],
      [26, 44],
      [31, 68],
      [4, 23],
      [64, 97],
    ]),
  },
  {
    // 5 snakes (rows 2–3), 8 ladders (rows 2–6) — 23.56 rolls.
    id: "e4",
    snakes: S([
      [77, 55],
      [96, 80],
      [67, 31],
      [24, 6],
      [59, 37],
    ]),
    ladders: L([
      [11, 72],
      [23, 42],
      [51, 91],
      [48, 87],
      [57, 85],
      [5, 32],
      [27, 56],
      [4, 26],
    ]),
  },
  {
    // 5 snakes (rows 2–3), 8 ladders (rows 2–6) — 24.91 rolls.
    id: "e5",
    snakes: S([
      [55, 40],
      [38, 4],
      [65, 46],
      [41, 16],
      [22, 3],
    ]),
    ladders: L([
      [13, 35],
      [58, 94],
      [12, 34],
      [68, 87],
      [63, 83],
      [33, 92],
      [42, 80],
      [31, 91],
    ]),
  },
  {
    // 6 snakes (rows 2–3), 7 ladders (rows 2–6) — 26.08 rolls.
    id: "e6",
    snakes: S([
      [86, 68],
      [84, 60],
      [25, 5],
      [82, 57],
      [32, 14],
      [30, 7],
    ]),
    ladders: L([
      [15, 54],
      [42, 80],
      [35, 83],
      [4, 22],
      [9, 70],
      [71, 93],
      [66, 88],
    ]),
  },
];

const HARD: readonly LaddersBoard[] = [
  {
    // 7 snakes (rows 2–5), 7 ladders (rows 2–3) — 39.06 rolls.
    id: "h1",
    snakes: S([
      [52, 9],
      [60, 38],
      [40, 17],
      [55, 39],
      [84, 57],
      [66, 11],
      [51, 5],
    ]),
    ladders: L([
      [70, 92],
      [8, 34],
      [6, 26],
      [35, 56],
      [41, 65],
      [63, 94],
      [24, 45],
    ]),
  },
  {
    // 7 snakes (rows 2–5), 6 ladders (rows 2–4) — 38.24 rolls.
    id: "h2",
    snakes: S([
      [98, 73],
      [65, 30],
      [53, 2],
      [72, 40],
      [80, 42],
      [75, 50],
      [49, 11],
    ]),
    ladders: L([
      [7, 45],
      [16, 44],
      [54, 84],
      [51, 91],
      [4, 21],
      [33, 62],
    ]),
  },
  {
    // 8 snakes (rows 2–5), 7 ladders (rows 2–4) — 37.69 rolls.
    id: "h3",
    snakes: S([
      [53, 8],
      [38, 18],
      [58, 5],
      [85, 57],
      [52, 30],
      [90, 55],
      [79, 41],
      [97, 73],
    ]),
    ladders: L([
      [56, 94],
      [34, 54],
      [14, 46],
      [3, 36],
      [39, 78],
      [70, 93],
      [80, 96],
    ]),
  },
  {
    // 8 snakes (rows 2–5), 6 ladders (rows 2–4) — 38.74 rolls.
    id: "h4",
    snakes: S([
      [35, 2],
      [69, 43],
      [94, 61],
      [65, 16],
      [34, 9],
      [79, 23],
      [71, 49],
      [96, 64],
    ]),
    ladders: L([
      [66, 88],
      [57, 99],
      [3, 42],
      [7, 29],
      [32, 67],
      [19, 41],
    ]),
  },
  {
    // 7 snakes (rows 2–5), 6 ladders (rows 2–4) — 36.94 rolls.
    id: "h5",
    snakes: S([
      [55, 7],
      [82, 67],
      [91, 51],
      [87, 44],
      [95, 78],
      [47, 16],
      [41, 11],
    ]),
    ladders: L([
      [57, 80],
      [65, 96],
      [32, 54],
      [5, 23],
      [22, 61],
      [52, 92],
    ]),
  },
  {
    // 7 snakes (rows 3–5), 6 ladders (rows 2–4) — 39.56 rolls.
    id: "h6",
    snakes: S([
      [75, 34],
      [42, 9],
      [80, 44],
      [97, 59],
      [52, 12],
      [56, 24],
      [79, 21],
    ]),
    ladders: L([
      [6, 33],
      [39, 76],
      [70, 90],
      [68, 92],
      [63, 96],
      [30, 48],
    ]),
  },
];

/** Every level's maps — what the engine draws a game's board from (`rng.pick`). */
export const LADDERS_BOARDS_BY_LEVEL: Readonly<Record<LaddersLevel, readonly LaddersBoard[]>> = {
  easy: EASY,
  medium: MEDIUM,
  hard: HARD,
};

/**
 * EVERY SHIPPED MAP, all levels (medium, easy, hard — append-only, order included): what the
 * validators and the drawing tests run over, and what `boardById` finds a saved game's map in.
 */
export const LADDERS_BOARDS: readonly LaddersBoard[] = [...MEDIUM, ...EASY, ...HARD];

/** The level whose set holds the map with this id, or undefined. */
export function levelOfBoard(id: string): LaddersLevel | undefined {
  for (const level of ["easy", "medium", "hard"] as const) {
    if (LADDERS_BOARDS_BY_LEVEL[level].some((b) => b.id === id)) return level;
  }
  return undefined;
}

/** The shipped board with this id, or undefined. */
export function boardById(id: string): LaddersBoard | undefined {
  return LADDERS_BOARDS.find((b) => b.id === id);
}
